import * as React from "react";
import { ResponsiveContainer, Tooltip } from "recharts";
import { cn } from "@/lib/utils";

// shadcn chart composition: owned source, Recharts primitives and token-based colors.
export type ChartConfig = Record<string, { label: string; color: string }>;
export function ChartContainer({
  className,
  config,
  children,
}: React.ComponentProps<"div"> & {
  config: ChartConfig;
  children: React.ComponentProps<typeof ResponsiveContainer>["children"];
}) {
  const variables = Object.fromEntries(
    Object.entries(config).map(([key, value]) => [
      `--color-${key}`,
      value.color,
    ]),
  ) as React.CSSProperties;
  return (
    <div
      data-slot="chart"
      style={variables}
      className={cn(
        "chart-container h-64 min-h-64 w-full text-xs [&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground [&_.recharts-cartesian-grid_line]:stroke-border [&_.recharts-layer]:outline-none [&_.recharts-surface]:outline-none",
        className,
      )}
    >
      <ResponsiveContainer width="100%" height="100%">
        {children}
      </ResponsiveContainer>
    </div>
  );
}
export const ChartTooltip = Tooltip;
export function ChartTooltipContent({
  active,
  payload,
  label,
  formatter,
  labelFormatter,
  nameFormatter,
}: {
  active?: boolean;
  payload?: readonly {
    name?: string | number;
    value?: string | number | null;
    color?: string;
    dataKey?: string | number;
  }[];
  label?: React.ReactNode;
  labelFormatter?: (label: React.ReactNode) => React.ReactNode;
  nameFormatter?: (name: string | number | undefined) => React.ReactNode;
  formatter?: (
    value: string | number | null | undefined,
    key?: string,
  ) => string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="min-w-40 rounded-lg border border-border bg-card px-3 py-2.5 text-xs shadow-xl">
      <div className="mb-2 font-semibold">
        {labelFormatter ? labelFormatter(label) : label}
      </div>
      {payload.map((item, i) => (
        <div key={i} className="flex items-center gap-2 py-0.5">
          <span
            className="size-2 rounded-sm"
            style={{ background: item.color }}
          />
          <span className="text-muted-foreground">
            {nameFormatter ? nameFormatter(item.name) : item.name}
          </span>
          <span className="ms-auto ps-4 font-semibold tabular-nums">
            {formatter
              ? formatter(item.value, String(item.dataKey ?? ""))
              : item.value}
          </span>
        </div>
      ))}
    </div>
  );
}
